import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, User, LogOut, Heart, Search, MessageSquare, Shield } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useMessaging } from "../../context/MessagingContext";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const { unreadCount } = useMessaging();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Главная" },
    { href: "/experts", label: "Сообщество" },
    { href: "/articles", label: "Статьи" },
    { href: "/how-it-works", label: "Как это работает" },
    { href: "/pricing", label: "Тарифы" }
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
    setMobileMenuOpen(false);
  };

  return (
    <header
      id="header_main"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-neutral-950/90 backdrop-blur-xl border-b border-neutral-800/30"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3 shrink-0 mr-8">
            <div className="w-9 h-9 rounded-md bg-primary-600 flex items-center justify-center">
              <span className="text-white font-medium text-lg">О</span>
            </div>
            <span className="hidden sm:block text-lg font-extralight text-neutral-200 tracking-wide">
              Обстучим
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-lg font-extralight transition-colors whitespace-nowrap ${
                  location.pathname === link.href
                    ? "text-primary-400"
                    : "text-neutral-400 hover:text-neutral-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/experts"
              className="p-2 text-neutral-400 hover:text-neutral-100 transition-colors"
            >
              <Search className="w-5 h-5" />
            </Link>

            {user ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/messages"
                  className={`relative p-2 transition-colors ${
                    location.pathname === "/messages"
                      ? "text-primary-400"
                      : "text-neutral-400 hover:text-neutral-100"
                  }`}
                >
                  <MessageSquare className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary-500 text-white text-lg font-medium flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/favorites"
                  className="p-2 text-neutral-400 hover:text-neutral-100 transition-colors"
                >
                  <Heart className="w-5 h-5" />
                </Link>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    className={`p-2 transition-colors ${
                      location.pathname === "/admin"
                        ? "text-primary-400"
                        : "text-neutral-400 hover:text-neutral-100"
                    }`}
                    title="Админ-панель"
                  >
                    <Shield className="w-5 h-5" />
                  </Link>
                )}
                <Link
                  to={user.role === "admin" ? "/admin" : user.role === "expert" ? "/expert/dashboard" : "/client/dashboard"}  
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-neutral-800/60 hover:bg-neutral-800 transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span className="text-lg font-extralight">{user.name?.split(" ")[0]}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-2 text-neutral-400 hover:text-neutral-100 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="px-4 py-2 text-lg font-extralight text-neutral-300 hover:text-white transition-colors"
                >
                  Войти
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                >
                  Регистрация
                </Link>
              </div>
            )}
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-neutral-300"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-neutral-900 border-t border-neutral-800/50"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-md text-lg font-extralight transition-colors ${
                    location.pathname === link.href
                      ? "bg-primary-600/10 text-primary-400"
                      : "text-neutral-300 hover:bg-neutral-800"
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-4 border-t border-neutral-800/50 space-y-2">
                {user ? (
                  <>
                    <Link
                      to="/messages"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-md text-neutral-300 hover:bg-neutral-800"
                    >
                      <MessageSquare className="w-5 h-5" />
                      <span className="text-lg font-extralight">Сообщения</span>
                      {unreadCount > 0 && (
                        <span className="ml-auto px-2 py-0.5 rounded-full bg-primary-500 text-white text-lg font-medium">
                          {unreadCount}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/favorites"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-md text-neutral-300 hover:bg-neutral-800"
                    >
                      <Heart className="w-5 h-5" />
                      <span className="text-lg font-extralight">Избранное</span>
                    </Link>
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-md text-neutral-300 hover:bg-neutral-800"
                      >
                        <Shield className="w-5 h-5" />
                        <span className="text-lg font-extralight">Админ-панель</span>
                      </Link>
                    )}
                    <Link
                      to={user.role === "admin" ? "/admin" : user.role === "expert" ? "/expert/dashboard" : "/client/dashboard"}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-md text-neutral-300 hover:bg-neutral-800"
                    >
                      <User className="w-5 h-5" />
                      <span className="text-lg font-extralight">Личный кабинет</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-md text-neutral-300 hover:bg-neutral-800"
                    >
                      <LogOut className="w-5 h-5" />
                      <span className="text-lg font-extralight">Выйти</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-md text-center text-lg font-extralight text-neutral-300 hover:bg-neutral-800"
                    >
                      Войти
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-md text-center bg-primary-600 text-white text-lg font-extralight"
                    >
                      Регистрация
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}